import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';

import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import { axiosClient } from '@/services/BaseApi.service';

import {
  reportsEndDateAtom,
  reportsOrgLabelAtom,
  reportsStartDateAtom
} from '../../ReportsTemplate.hooks';
import { getTemplateValues } from '../../ReportsTemplate.utils';
import type {
  EventMetricsType,
  GroupedEventsMetricsType
} from '../ReportsPage.types';

export const useFetchReportsEventsData = ({
  groupByField,
  eventType
}: {
  groupByField: string;
  eventType: 'network' | 'log';
}) => {
  const eventTypeFormatted =
    eventType === 'network' ? 'network_events' : 'log_events';
  const [selectedOrganization] = useAtom(reportsOrgLabelAtom);
  const [startDate] = useAtom(reportsStartDateAtom);
  const [endDate] = useAtom(reportsEndDateAtom);
  const diff = getTemplateValues();
  const minStartDate =
    diff < 7
      ? dayjs(startDate)
          .subtract(7 - diff, 'day')
          .format(YEAR_MONTH_DAY_FORMAT)
      : startDate;

  const queryFn = () => {
    return axiosClient
      .get(
        `/organizations/${selectedOrganization}/${eventTypeFormatted}/per_day/${groupByField}?start_date=${minStartDate}&end_date=${endDate}`
      )
      .then((resp) => resp.data);
  };

  const { data } = useQuery<GroupedEventsMetricsType[], AxiosError>({
    queryKey: [
      'events',
      selectedOrganization,
      minStartDate,
      endDate,
      eventType,
      groupByField
    ],
    queryFn,
    staleTime: 60 * 60 * 1000,
    enabled:
      selectedOrganization !== undefined &&
      startDate !== null &&
      endDate !== null &&
      selectedOrganization !== ''
  });

  return { data };
};

// Metrics never shown over time, so don't need to adjust time frame for daily reports
export const useFetchReportsEventsMetricsData = ({
  metricField,
  eventType
}: {
  metricField: string;
  eventType: 'network' | 'log';
}) => {
  const eventTypeFormatted =
    eventType === 'network' ? 'net_metric' : 'log_metric';
  const [selectedOrganization] = useAtom(reportsOrgLabelAtom);
  const [startDate] = useAtom(reportsStartDateAtom);
  const [endDate] = useAtom(reportsEndDateAtom);

  const queryFn = () => {
    return axiosClient
      .get(
        `/metrics/${eventTypeFormatted}/${metricField}?organization_label=${selectedOrganization}&start_date=${startDate}&end_date=${endDate}`
      )
      .then((resp) => resp.data);
  };

  const { data } = useQuery<EventMetricsType[], AxiosError>({
    queryKey: [
      'events',
      selectedOrganization,
      startDate,
      endDate,
      eventType,
      metricField
    ],
    queryFn,
    staleTime: 60 * 60 * 1000,
    enabled:
      selectedOrganization !== undefined &&
      startDate !== null &&
      endDate !== null &&
      selectedOrganization !== ''
  });

  return { data };
};

export default useFetchReportsEventsData;
